import { t, Trans } from '@lingui/macro';
import { useState } from 'react';
import {
    Button,
    Collapse,
    TextField as MuiTextField,
    Typography,
    MenuItem,
    FormControlLabel,
    Switch,
    IconButton,
} from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import _ from 'lodash';

import { INTERNAL_ID_FIELD } from '../../form/FormConstants';
import { RowFlexLayout } from '../../layout';
import useTestInstanceTemplateStyles from './TestInstanceTemplateStyles';

export default function TestInstanceTemplateFieldEditor({
    sectionIndex,
    rowIndex,
    field,
    fieldIndex,
    inputTypes,
    onFieldChange,
    onFieldTypeChange,
    onRemoveField,
    onOptionChange,
    onRemoveOption,
    onAddOption,
}) {
    const OPTION_ROW_GAP = 4;
    const OPTION_ROW_MARGIN_BOTTOM = 0.5;
    const classes = useTestInstanceTemplateStyles();
    const optionList = _.get(field, 'options', []) || [];
    const [optionsExpanded, setOptionsExpanded] = useState(true);
    const fieldType = field?.type || 'text';
    const isNumberType = fieldType === 'number' || fieldType === 'integer';
    const isOptionType = fieldType === 'select' || fieldType === 'radio';
    const isBooleanType = fieldType === 'boolean';
    const isTextLikeType = fieldType === 'text' || fieldType === 'hostname';
    const handleRemoveFieldClick = () => {
        onRemoveField(sectionIndex, rowIndex, fieldIndex);
    };

    const handleRemoveOptionClick = (optionIndex) => () => {
        onRemoveOption(sectionIndex, rowIndex, fieldIndex, optionIndex);
    };

    return (
        <div className={classes.fieldContainer}>
            <FormControlLabel
                className={classes.fieldEnabledToggle}
                control={
                    <Switch
                        color="primary"
                        checked={!!field?.required}
                        onChange={(_event, checked) =>
                            onFieldChange(sectionIndex, rowIndex, fieldIndex, 'required', checked)
                        }
                    />
                }
                label={<Trans>Enabled</Trans>}
            />
            <div>
                <span className={classes.inlineField}>
                    <MuiTextField
                        label={t`Name`}
                        value={field?.title || ''}
                        onChange={(event) =>
                            onFieldChange(sectionIndex, rowIndex, fieldIndex, 'title', event.target.value)
                        }
                        required
                    />
                </span>

                <span className={classes.inlineField}>
                    <MuiTextField
                        label={t`Input Type`}
                        value={fieldType}
                        onChange={(event) => onFieldTypeChange(sectionIndex, rowIndex, fieldIndex, event.target.value)}
                        select
                    >
                        {inputTypes.map((inputType) => (
                            <MenuItem key={inputType} value={inputType}>
                                {inputType}
                            </MenuItem>
                        ))}
                    </MuiTextField>
                </span>

                {isTextLikeType && (
                    <span className={classes.inlineField}>
                        <MuiTextField
                            label={t`Default`}
                            value={_.isNil(field?.default) ? '' : field.default}
                            type="text"
                            onChange={(event) =>
                                onFieldChange(sectionIndex, rowIndex, fieldIndex, 'default', event.target.value)
                            }
                        />
                    </span>
                )}

                {isNumberType && (
                    <span className={classes.inlineField}>
                        <MuiTextField
                            label={t`Default`}
                            value={_.isNil(field?.default) ? '' : field.default}
                            type="number"
                            onChange={(event) =>
                                onFieldChange(sectionIndex, rowIndex, fieldIndex, 'default', event.target.value)
                            }
                        />
                    </span>
                )}

                {isNumberType && (
                    <span className={classes.inlineField}>
                        <MuiTextField
                            label={t`Min`}
                            type="number"
                            value={_.isNil(field?.min) ? '' : field.min}
                            onChange={(event) =>
                                onFieldChange(sectionIndex, rowIndex, fieldIndex, 'min', event.target.value)
                            }
                        />
                    </span>
                )}

                {isNumberType && (
                    <span className={classes.inlineField}>
                        <MuiTextField
                            label={t`Max`}
                            type="number"
                            value={_.isNil(field?.max) ? '' : field.max}
                            onChange={(event) =>
                                onFieldChange(sectionIndex, rowIndex, fieldIndex, 'max', event.target.value)
                            }
                        />
                    </span>
                )}

                {isNumberType && (
                    <span className={classes.inlineField}>
                        <MuiTextField
                            label={t`Units`}
                            value={field?.units || ''}
                            onChange={(event) =>
                                onFieldChange(sectionIndex, rowIndex, fieldIndex, 'units', event.target.value)
                            }
                        />
                    </span>
                )}

                {isBooleanType && (
                    <span className={`${classes.inlineField} ${classes.booleanField}`}>
                        <Typography variant="caption" color="textSecondary" className={classes.booleanFieldLabel}>
                            <Trans>Default</Trans>
                        </Typography>
                        <FormControlLabel
                            style={{ marginLeft: 0, marginTop: 0 }}
                            control={
                                <Switch
                                    color="primary"
                                    checked={!!field?.default}
                                    onChange={(_event, checked) =>
                                        onFieldChange(sectionIndex, rowIndex, fieldIndex, 'default', checked)
                                    }
                                />
                            }
                            label={field?.default ? t`True` : t`False`}
                        />
                    </span>
                )}

                {isOptionType && (
                    <span className={classes.inlineField}>
                        <MuiTextField
                            label={t`Default`}
                            value={_.isNil(field?.default) ? '' : field.default}
                            onChange={(event) =>
                                onFieldChange(sectionIndex, rowIndex, fieldIndex, 'default', event.target.value)
                            }
                            select
                        >
                            {(_.get(field, 'options', []) || []).map((option) => (
                                <MenuItem key={option?.[INTERNAL_ID_FIELD]} value={option?.value || ''}>
                                    {option?.name || option?.value || ''}
                                </MenuItem>
                            ))}
                        </MuiTextField>
                    </span>
                )}
                {fieldType === 'text' && (
                    <span className={classes.inlineField}>
                        <MuiTextField
                            label={t`Size`}
                            type="number"
                            value={_.isNil(field?.maxlength) ? '' : field.maxlength}
                            onChange={(event) =>
                                onFieldChange(sectionIndex, rowIndex, fieldIndex, 'maxlength', event.target.value)
                            }
                        />
                    </span>
                )}

                <IconButton
                    aria-label={t`Remove field`}
                    onClick={handleRemoveFieldClick}
                    className={`${classes.removeFieldButton} ${classes.primaryIconButton}`}
                    color="primary"
                >
                    <RemoveCircleOutlineIcon />
                </IconButton>

                <div className={classes.clearBoth} />
            </div>

            {isOptionType && (
                <div className={classes.optionSection}>
                    <div className={classes.optionHeader}>
                        <Typography variant="body2" className={classes.optionHeaderLabel}>
                            <Trans>Manage Options</Trans>
                        </Typography>
                        <IconButton
                            aria-label={optionsExpanded ? t`Collapse options` : t`Expand options`}
                            onClick={() => setOptionsExpanded((expanded) => !expanded)}
                            size="small"
                        >
                            {optionsExpanded ? (
                                <ExpandLessIcon fontSize="small" />
                            ) : (
                                <ExpandMoreIcon fontSize="small" />
                            )}
                        </IconButton>
                    </div>

                    <Collapse in={optionsExpanded} className={classes.optionList}>
                        {optionList.map((option, optionIndex) => (
                            <RowFlexLayout
                                key={option?.[INTERNAL_ID_FIELD]}
                                vBottom
                                gridGap={OPTION_ROW_GAP}
                                wrap
                                marginBottom={OPTION_ROW_MARGIN_BOTTOM}
                            >
                                <MuiTextField
                                    label={t`Option Name`}
                                    value={option?.name || ''}
                                    onChange={(event) =>
                                        onOptionChange(
                                            sectionIndex,
                                            rowIndex,
                                            fieldIndex,
                                            optionIndex,
                                            'name',
                                            event.target.value
                                        )
                                    }
                                />

                                <MuiTextField
                                    label={t`Option Value`}
                                    value={option?.value || ''}
                                    onChange={(event) =>
                                        onOptionChange(
                                            sectionIndex,
                                            rowIndex,
                                            fieldIndex,
                                            optionIndex,
                                            'value',
                                            event.target.value
                                        )
                                    }
                                />

                                <IconButton
                                    color="primary"
                                    className={classes.primaryIconButton}
                                    aria-label={t`Remove option`}
                                    onClick={handleRemoveOptionClick(optionIndex)}
                                >
                                    <RemoveCircleOutlineIcon />
                                </IconButton>
                            </RowFlexLayout>
                        ))}
                    </Collapse>
                    <Button
                        size="small"
                        color="primary"
                        onClick={() => onAddOption(sectionIndex, rowIndex, fieldIndex)}
                    >
                        <Trans>Add a select Option</Trans>
                    </Button>
                </div>
            )}
        </div>
    );
}
